import * as React from 'react'
import { useState, useCallback } from 'react'
import { motion } from 'motion/react'
import { Check, MessageCircleDashed, Send } from 'lucide-react'
import { cn } from '../../lib/utils'
import { SIZE_CONFIG } from './TurnCard'

// ============================================================================
// Types
// ============================================================================

export interface AskUserQuestionOption {
  label: string
  description: string
}

export interface AskUserQuestion {
  question: string
  header: string
  multiSelect?: boolean
  options: AskUserQuestionOption[]
}

export interface AskUserQuestionCardProps {
  questions: AskUserQuestion[]
  isAnswered: boolean
  selectedAnswers?: Record<number, string[]>
  onSubmit?: (answers: Record<number, { selected: string[]; otherText?: string }>) => void
}

// ============================================================================
// AskUserQuestionCard
// ============================================================================

export function AskUserQuestionCard({
  questions,
  isAnswered,
  selectedAnswers,
  onSubmit,
}: AskUserQuestionCardProps) {
  // Local selection state: questionIndex -> set of selected labels
  const [selections, setSelections] = useState<Record<number, Set<string>>>(() => {
    if (selectedAnswers) {
      const init: Record<number, Set<string>> = {}
      for (const [k, v] of Object.entries(selectedAnswers)) {
        init[Number(k)] = new Set(v)
      }
      return init
    }
    return {}
  })

  // Local "Other" text state per question
  const [otherTexts, setOtherTexts] = useState<Record<number, string>>({})

  // Non-interactive when answered or no submit handler
  const isInteractive = !isAnswered && !!onSubmit

  const toggleOption = useCallback((questionIndex: number, label: string) => {
    if (!isInteractive) return
    setSelections(prev => {
      const current = new Set(prev[questionIndex] ?? [])
      if (current.has(label)) {
        current.delete(label)
      } else {
        current.add(label)
      }
      return { ...prev, [questionIndex]: current }
    })
  }, [isInteractive])

  const handleOtherTextChange = useCallback((questionIndex: number, text: string) => {
    if (!isInteractive) return
    setOtherTexts(prev => ({ ...prev, [questionIndex]: text }))
  }, [isInteractive])

  const handleSubmit = useCallback(() => {
    if (!onSubmit) return
    const answers: Record<number, { selected: string[]; otherText?: string }> = {}
    for (let i = 0; i < questions.length; i++) {
      const selected = Array.from(selections[i] ?? [])
      const otherText = otherTexts[i]?.trim()
      answers[i] = { selected, ...(otherText ? { otherText } : {}) }
    }
    onSubmit(answers)
  }, [onSubmit, questions.length, selections, otherTexts])

  const hasAnySelection = Object.values(selections).some(s => s.size > 0) ||
    Object.values(otherTexts).some(t => t.trim().length > 0)

  return (
    <div className="pl-4 pr-2 pt-2.5 pb-1.5 border-l-2 border-accent/30 ml-[13px]">
      {/* Header */}
      <div className={cn("flex items-center gap-2 text-muted-foreground pb-2", SIZE_CONFIG.fontSize)}>
        <MessageCircleDashed className={cn(SIZE_CONFIG.iconSize, "shrink-0 text-accent")} />
        <span>Clarifying Questions</span>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {questions.map((q, qIndex) => (
          <motion.div
            key={`q-${qIndex}`}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: qIndex * 0.05 }}
            className="space-y-2"
          >
            {/* Question header badge + text */}
            <div className="space-y-1">
              <span className={cn(
                "inline-block px-1.5 py-0.5 rounded text-[11px] font-medium",
                "bg-accent/10 text-accent border border-accent/20"
              )}>
                {q.header}
              </span>
              <p className={cn("text-foreground", SIZE_CONFIG.fontSize)}>{q.question}</p>
            </div>

            {/* Option cards */}
            <div className="space-y-1.5">
              {q.options.map((option, oIndex) => {
                const isSelected = isAnswered
                  ? selectedAnswers?.[qIndex]?.includes(option.label) ?? false
                  : selections[qIndex]?.has(option.label) ?? false

                return (
                  <motion.button
                    key={`o-${oIndex}`}
                    type="button"
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: qIndex * 0.05 + oIndex * 0.03 }}
                    disabled={!isInteractive}
                    onClick={() => toggleOption(qIndex, option.label)}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-md border transition-colors",
                      "flex items-start gap-2.5",
                      isSelected
                        ? "border-accent/50 bg-accent/5"
                        : isInteractive
                          ? "border-border/50 bg-muted/30 hover:border-border hover:bg-muted/50"
                          : "border-border/50 bg-muted/30",
                      !isInteractive && !isSelected && "opacity-40",
                      !isInteractive && "cursor-default"
                    )}
                  >
                    {/* Selection indicator */}
                    <div className={cn(
                      "mt-0.5 shrink-0 w-4 h-4 border flex items-center justify-center transition-colors",
                      q.multiSelect !== false ? "rounded-sm" : "rounded-full",
                      isSelected
                        ? "border-accent bg-accent text-accent-foreground"
                        : "border-muted-foreground/30"
                    )}>
                      {isSelected && (q.multiSelect !== false
                        ? <Check className="w-3 h-3" />
                        : <div className="w-2 h-2 rounded-full bg-current" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className={cn("font-medium text-foreground", SIZE_CONFIG.fontSize)}>
                        {option.label}
                      </div>
                      <div className={cn("text-muted-foreground text-[12px] mt-0.5 leading-snug")}>
                        {option.description}
                      </div>
                    </div>
                  </motion.button>
                )
              })}

              {/* Other (freeform) option */}
              {isInteractive && (
                <motion.div
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: qIndex * 0.05 + q.options.length * 0.03 }}
                  className={cn(
                    "px-3 py-2 rounded-md border transition-colors",
                    "border-border/50 bg-muted/30",
                    otherTexts[qIndex]?.trim() && "border-accent/50 bg-accent/5"
                  )}
                >
                  <div className={cn("font-medium text-foreground mb-1", SIZE_CONFIG.fontSize)}>
                    Other
                  </div>
                  <input
                    type="text"
                    placeholder="Type your own answer..."
                    value={otherTexts[qIndex] ?? ''}
                    onChange={(e) => handleOtherTextChange(qIndex, e.target.value)}
                    className={cn(
                      "w-full bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground/50",
                      SIZE_CONFIG.fontSize
                    )}
                  />
                </motion.div>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Submit button */}
      {isInteractive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: questions.length * 0.05 + 0.1 }}
          className="mt-3 flex justify-end"
        >
          <button
            type="button"
            disabled={!hasAnySelection}
            onClick={handleSubmit}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors",
              hasAnySelection
                ? "bg-accent text-accent-foreground hover:bg-accent/90"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            )}
          >
            <Send className="w-3 h-3" />
            Submit
          </button>
        </motion.div>
      )}

      {/* Answered state indicator */}
      {isAnswered && (
        <div className={cn("mt-2 flex items-center gap-1.5 text-muted-foreground/70", SIZE_CONFIG.fontSize)}>
          <Check className={cn(SIZE_CONFIG.iconSize, "text-accent")} />
          <span>Answered</span>
        </div>
      )}
    </div>
  )
}
